import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, CircularProgress, IconButton, Tooltip} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {createSpeechStreamingClient} from './SpeechStreamingClient';

/**
 * Voice-mode mic control for chat input bars.
 * @param {Object} props
 * @param {string} props.value
 * @param {Function} props.onChange
 * @param {Function} props.onVoiceModeChange
 * @param {Function} props.onTranscriptPreview
 * @param {Function=} props.onWarning
 * @param {boolean=} props.disabled
 * @return {JSX.Element|null}
 */
function MicButton({value, onChange, onVoiceModeChange, onTranscriptPreview, onWarning, disabled = false}) {
  const sttEnabled = useSelector((state) => {
    return state.app.authenticationStatus.sttEnabled;
  });
  const [mode, setMode] = useState('inactive');
  const [isStarting, setIsStarting] = useState(false);
  const clientRef = useRef(null);
  const finalRef = useRef('');
  const interimRef = useRef('');
  const warningRef = useRef('');
  // When the user clicks ✓ while recording, we wait for the streaming client's
  // tail-flush to finish (onEnd) before committing to the text field — otherwise
  // the last partial→final transition can be dropped.
  const pendingActionRef = useRef(/** @type {null | 'accept' | 'discard'} */ (null));

  const showWarning = (message, {skipIfActive = false} = {}) => {
    if (skipIfActive && warningRef.current) {
      return;
    }
    warningRef.current = message;
    onWarning && onWarning(message);
  };

  const browserOk = useMemo(() => {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      window.AudioWorkletNode &&
      window.WebSocket
    );
  }, []);

  // Join transcript pieces.
  const joinText = (...parts) => {
    return parts
      .map((p) => {
        return (p || '').trim();
      })
      .filter(Boolean)
      .join(' ');
  };

  // Includes any text the user had typed before starting voice mode so it stays
  // visible during recording instead of being hidden by the transcript preview.
  const getDisplayText = () => {
    return joinText(value, finalRef.current, interimRef.current);
  };

  const emitPreview = () => {
    onTranscriptPreview(getDisplayText());
  };

  // Mic Related Errors
  const micErrorMessage = (err) => {
    switch (err && err.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Microphone access is blocked. Allow microphone access in your browser settings to use voice input.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'No microphone was found. Connect a microphone and try again.';
      case 'NotReadableError':
        return 'Your microphone is in use by another app. Close it and try again.';
      default:
        return 'Could not start voice input. Please try again.';
    }
  };

  // Connection Related Errors
  const streamErrorMessage = (err) => {
    switch (err && err.code) {
      case 'timeout':
        return err.message;
      case 'stt-unavailable':
        return 'Voice input is temporarily unavailable. Please try again later.';
      case 'ws_error':
        return 'Lost connection to the voice service. Check your network and try again.';
      case 'stt_error':
      default:
        return 'Something went wrong with voice input. Please try again.';
    }
  };

  const startRecording = async () => {
    if (disabled || isStarting) {
      return;
    }
    setIsStarting(true);
    showWarning('');
    finalRef.current = '';
    interimRef.current = '';
    emitPreview();

    clientRef.current = createSpeechStreamingClient({
      onPartial: (t) => {
        interimRef.current = t;
        emitPreview();
      },
      onFinal: (t) => {
        finalRef.current = joinText(finalRef.current, t);
        interimRef.current = '';
        emitPreview();
      },
      onError: (err) => {
        console.error('[SpeechToText] MicButton: error', err);
        clientRef.current?.stop();
        clientRef.current = null;
        showWarning(streamErrorMessage(err), {skipIfActive: err && err.code === 'timeout'});
        // Timeout still gives the user whatever they said; other errors discard.
        if (err && err.code === 'timeout') {
          pendingActionRef.current = 'accept';
          setMode('finishing');
        } else {
          pendingActionRef.current = null;
          finalizeDiscard();
        }
        setIsStarting(false);
      },
      onEnd: () => {
        // If the session ended with an in-progress turn that never finalized
        // (e.g. user pushes stop mid-sentence), commit that leftover so the inprogress words
        // aren't lost.
        const leftover = interimRef.current.trim();
        if (leftover) {
          finalRef.current = joinText(finalRef.current, leftover);
          interimRef.current = '';
          emitPreview();
        }
        // Apply the action the user queued when they stopped recording.
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        if (action === 'accept') {
          finalizeAccept();
        } else if (action === 'discard') {
          finalizeDiscard();
        }
      },
    });

    try {
      await clientRef.current.start();
      setMode('recording');
      onVoiceModeChange(true);
    } catch (err) {
      console.error('[SpeechToText] MicButton: start failed', err);
      showWarning(micErrorMessage(err));
      onVoiceModeChange(false);
      setMode('inactive');
    } finally {
      setIsStarting(false);
    }
  };

  // Stop the streaming client and queue the action that should run once the
  // server drains the tail (handled in onEnd).
  const requestStop = (action) => {
    if (pendingActionRef.current) {
      return;
    }
    pendingActionRef.current = action;
    setMode('finishing');
    clientRef.current?.stop();
    clientRef.current = null;
  };

  const finalizeAccept = () => {
    // getDisplayText already includes the prior `value`, so just replace.
    const text = getDisplayText();
    if (text) {
      onChange(text);
    }
    finalRef.current = '';
    interimRef.current = '';
    onTranscriptPreview('');
    setMode('inactive');
    onVoiceModeChange(false);
  };

  const finalizeDiscard = () => {
    finalRef.current = '';
    interimRef.current = '';
    onTranscriptPreview('');
    setMode('inactive');
    onVoiceModeChange(false);
  };

  useEffect(() => {
    return () => {
      clientRef.current?.stop();
      clientRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (mode !== 'recording') {
      return undefined;
    }
    const handleVisibilityChange = () => {
      // Tab backgrounded mid-recording: keep what they said rather than losing it.
      if (document.hidden) {
        requestStop('accept');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [mode]);

  if (!sttEnabled || !browserOk) {
    return null;
  }

  if (mode === 'recording' || mode === 'finishing') {
    const isFinishing = mode === 'finishing';
    return (
      <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0}}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mr: 0.5,
            color: isFinishing ? 'text.disabled' : 'error.main',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'currentColor',
              animation: isFinishing ? 'none' : 'recPulse 1.2s ease-in-out infinite',
              '@keyframes recPulse': {
                '0%, 100%': {opacity: 1, transform: 'scale(1)'},
                '50%': {opacity: 0.4, transform: 'scale(0.7)'},
              },
            }}
          />
          {isFinishing ? 'Finishing' : 'Recording'}
        </Box>
        <Tooltip title='Cancel recording'>
          <span>
            <IconButton
              color='error'
              size='small'
              onClick={() => {
                return requestStop('discard');
              }}
              disabled={disabled || isFinishing}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Confirm transcript'>
          <span>
            <IconButton
              color='success'
              size='small'
              onClick={() => {
                return requestStop('accept');
              }}
              disabled={disabled || isFinishing}
            >
              {isFinishing ? <CircularProgress size={18} /> : <CheckIcon fontSize='small' />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Tooltip title='Voice input'>
      <span>
        <IconButton
          size='small'
          disabled={disabled || isStarting}
          onClick={startRecording}
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
          }}
        >
          {isStarting ? <CircularProgress size={18} /> : <MicIcon fontSize='small' />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default MicButton;
